import { LayoutViewMode } from '@rematter/modules/trade/types/scaleTickets';
import { Empty, EmptyProps, Loading, TouchButton } from '@rematter/ui-web';
import { LucideFrown } from 'lucide-react';

import { CameraFeedSidebar } from './CameraFeedSidebar';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface TicketGridLayoutProps {
  isLoading: boolean;
  ticketsLength: number;
  viewMode: LayoutViewMode;
  emptyPanelProps?: EmptyProps;
  errorPanelProps?: EmptyProps;
  hasError?: boolean;
  isCreating: boolean;
  hasActiveFilters: boolean;
  onCreateNew: () => void;
  onClearSearch: () => void;
}

export const TicketGridLayout: React.FC<
  React.PropsWithChildren<TicketGridLayoutProps>
> = ({
  children,
  isLoading,
  ticketsLength,
  viewMode,
  hasError,
  emptyPanelProps,
  errorPanelProps,
  isCreating,
  hasActiveFilters,
  onCreateNew,
  onClearSearch,
}) => {
  const t = useTranslations();

  if (isLoading) {
    return (
      <div className="@TicketGridLayout flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="h-full">
        <Empty
          className="h-full"
          title={t.scaleTicketPro.error}
          description={t.scaleTicketPro.errorDescription}
          centered={true}
          icon={<LucideFrown className="size-24 stroke-[1px] text-failure" />}
          {...errorPanelProps}
        />
      </div>
    );
  }

  if (ticketsLength === 0 && hasActiveFilters) {
    return (
      <div className="@TicketGridLayout h-full">
        <Empty
          className="h-full"
          title={t.scaleTicketPro.noSearchResults}
          description={t.scaleTicketPro.noSearchResultsHint}
          centered={true}
          action={
            <TouchButton
              variant="solid"
              color="primary"
              onPress={onClearSearch}
            >
              {t.scaleTicketPro.clearSearch}
            </TouchButton>
          }
          {...emptyPanelProps}
        />
      </div>
    );
  }

  if (ticketsLength === 0 && !hasActiveFilters) {
    return (
      <div className="@TicketGridLayout h-full">
        <Empty
          className="h-full"
          title={t.scaleTicketPro.noTickets}
          centered={true}
          action={
            <TouchButton
              variant="solid"
              color="primary"
              onPress={onCreateNew}
              isDisabled={isCreating}
            >
              {t.scaleTicketPro.createFirst}
            </TouchButton>
          }
          {...emptyPanelProps}
        />
      </div>
    );
  }

  if (viewMode === 'camera') {
    return (
      <div className="@TicketGridLayout flex min-h-0 flex-1">
        <div className="flex-1 overflow-auto p-4">{children}</div>
        <CameraFeedSidebar />
      </div>
    );
  }

  return (
    <div className="@TicketGridLayout flex-1 overflow-auto p-4">{children}</div>
  );
};
